import dayjs from 'dayjs';

/** Countdown urgency — red under an hour, amber under a day, green beyond. */
export type BroadcastExpiryTone = 'urgent' | 'warning' | 'safe';

export interface BroadcastExpiryLabel {
  text: string;
  tone: BroadcastExpiryTone;
}

type Translate = (key: string, vars?: Record<string, number>) => string;

/** True once `expires_at` has passed — the card then renders its expired form. */
export function isBroadcastExpired(expiresAt: string | null, now: number): boolean {
  if (expiresAt === null) return false;
  return dayjs(expiresAt).valueOf() <= now;
}

/**
 * The "Xd Xh left" countdown label, ported from the old panel's
 * `buildExpiryLabel` — same thresholds, same key selection, so the copy in
 * `common:broadcasts.*` is reused verbatim. Null when there is nothing to
 * count down to (no expiry, invalid, or already passed).
 */
export function buildBroadcastExpiryLabel(
  expiresAt: string,
  now: number,
  t: Translate,
): BroadcastExpiryLabel | null {
  const totalMinutes = dayjs(expiresAt).diff(now, 'minute');
  if (!Number.isFinite(totalMinutes) || totalMinutes <= 0) return null;

  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;

  let text: string;
  if (days > 0) {
    if (hours > 0) {
      text = t('broadcasts.expires_in_days_hours', { days, hours });
    } else if (days === 1) {
      text = t('broadcasts.expires_in_one', { count: 1 });
    } else {
      text = t('broadcasts.expires_in_other', { count: days });
    }
  } else if (hours > 0) {
    text = t('broadcasts.expires_in_hours_minutes', { hours, minutes });
  } else if (minutes === 1) {
    text = t('broadcasts.expires_in_one_minute');
  } else {
    text = t('broadcasts.expires_in_minutes', { minutes });
  }

  let tone: BroadcastExpiryTone = 'safe';
  if (totalMinutes <= 60) {
    tone = 'urgent';
  } else if (totalMinutes <= 60 * 24) {
    tone = 'warning';
  }

  return { text, tone };
}
